import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const NoticeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [notice, setNotice] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || Number(id) <= 0) {
      alert('잘못된 접근입니다.');
      navigate(-1);
      return;
    }

    fetchNotice();
    fetchBadges();
  }, [id]);

  const fetchNotice = async () => {
    try {
      const res = await axios.get(`/api/notice/${id}`, {
        withCredentials: true,
      });

      const data = res.data;

      if (!data || !data.notice) {
        alert('공지사항을 찾을 수 없습니다.');
        navigate(-1);
        return;
      }

      setNotice(data.notice);
    } catch (err) {
      alert(err.response?.data?.message || '공지사항을 불러오지 못했습니다.');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const [cartRes, wishlistRes] = await Promise.all([
        axios.get('/api/cart/count', { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
        axios.get('/api/wishlist/count', { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
      ]);

      setCartCount(Number(cartRes?.data?.count || 0));
      setWishlistCount(Number(wishlistRes?.data?.count || 0));
    } catch (err) {
      setCartCount(0);
      setWishlistCount(0);
    }
  };

  const downloadCoupon = async (couponId) => {
    try {
      const res = await axios.post(
        `/api/coupon/download/${couponId}`,
        {},
        { withCredentials: true }
      );

      alert(res.data?.message || '쿠폰이 발급되었습니다.');
    } catch (err) {
      if (err.response?.status === 401) {
        alert('로그인이 필요합니다.');
        return;
      }

      alert(err.response?.data?.message || '쿠폰 발급에 실패했습니다.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return String(dateString).slice(0, 16);
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ paddingTop: '70px', textAlign: 'center' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  if (!notice) return null;

  return (
    <>
      <style>{`
        .notice-content-prewrap {
          white-space: pre-wrap;
          word-break: break-word;
          line-height: 1.7;
        }

        @media (max-width: 768px) {
          .notice-page-wrap {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
        }
      `}</style>

      <div style={styles.page}>

        <div className="notice-page-wrap" style={styles.pageWrap}>
          <h5 style={styles.title}>{notice.title}</h5>
          <div style={styles.dateText}>{formatDate(notice.created_at)}</div>

          <div style={styles.contentBox} className="notice-content-prewrap">
            {notice.content || ''}
          </div>

          {notice.linked_coupon_id && Number(notice.coupon_active) === 1 && (
            <div style={styles.couponTicket}>
              <div style={styles.couponLeft}>
                <div style={styles.couponLabel}>
                  {notice.coupon_name || '공지사항 특별 쿠폰'}
                </div>

                <div style={styles.couponDiscount}>
                  {notice.discount_type === 'amount'
                    ? `${Number(notice.discount_value).toLocaleString()}원`
                    : `${notice.discount_value}%`}
                  <span style={styles.couponDiscountText}> 할인</span>
                </div>

                <div style={styles.couponCondition}>
                  {Number(notice.min_order_price || 0) > 0
                    ? `${Number(notice.min_order_price).toLocaleString()}원 이상 주문 시 사용 가능`
                    : '주문 시 사용 가능'}
                </div>

                <div style={styles.couponPeriod}>
                  발급일로부터 사용 가능
                </div>
              </div>

              <div style={styles.couponDivider} />

              <button
                type="button"
                style={styles.couponRight}
                onClick={() => downloadCoupon(notice.linked_coupon_id)}
              >
                <div style={styles.downloadIcon}>↓</div>
                <div style={styles.downloadText}>쿠폰 받기</div>
              </button>
            </div>
          )}

          <div style={styles.bottomRow}>
            <button
              type="button"
              style={styles.listBtn}
              onClick={() => navigate('/notice')}
            >
              목록
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  page: {
    background: '#fff',
    minHeight: '100vh',
    fontFamily: 'sans-serif',
    overflowX: 'hidden',
  },
  topBar: {
    background: '#ffffff',
    padding: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    fontWeight: 'bold',
    fontSize: '1.2rem',
    borderBottom: '1px solid #eee',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#000',
  },
  backButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: '#111',
    cursor: 'pointer',
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'center',
  },
  icons: {
    display: 'flex',
    gap: '1rem',
    fontSize: '1.4rem',
    alignItems: 'center',
  },
  iconBtn: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    color: '#111',
    display: 'flex',
    alignItems: 'center',
  },
  iconLink: {
    color: 'inherit',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  iconWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  badge: {
    position: 'absolute',
    top: '0px',
    right: '-8px',
    minWidth: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '#dc3545',
    color: '#fff',
    fontSize: '10px',
    lineHeight: '18px',
    textAlign: 'center',
    padding: 0,
    fontWeight: 'bold',
  },
  pageWrap: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: '10px 15px 24px',
    boxSizing: 'border-box',
  },
  title: {
    fontWeight: 'bold',
    marginTop: '1rem',
    marginBottom: '0.25rem',
    fontSize: '1.25rem',
    color: '#111',
  },
  dateText: {
    fontSize: '0.875rem',
    color: '#6c757d',
    marginBottom: '1rem',
  },
  contentBox: {
    border: '1px solid #dee2e6',
    borderRadius: '0.375rem',
    padding: '1rem',
    fontSize: '0.95rem',
    color: '#222',
    background: '#fff',
  },
  couponTicket: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    maxWidth: '520px',
    margin: '28px auto 0',
    minHeight: '150px',
    border: '1px solid #ead8c8',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #fffaf5 0%, #fff 55%, #fff7ef 100%)',
    boxShadow: '0 8px 22px rgba(120, 80, 40, 0.12)',
    overflow: 'hidden',
  },
  
  couponLeft: {
    flex: 1,
    padding: '26px 24px',
    position: 'relative',
  },
  
  couponLabel: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#9a6b48',
    marginBottom: '14px',
  },
  
  couponDiscount: {
    fontSize: '2.4rem',
    fontWeight: '800',
    color: '#111',
    lineHeight: 1.1,
    letterSpacing: '-1px',
  },
  
  couponDiscountText: {
    fontSize: '1.15rem',
    fontWeight: '700',
    marginLeft: '4px',
  },
  
  couponCondition: {
    marginTop: '10px',
    fontSize: '0.95rem',
    color: '#222',
  },
  
  couponPeriod: {
    display: 'inline-block',
    marginTop: '14px',
    padding: '5px 12px',
    borderRadius: '999px',
    border: '1px solid #e4cbb8',
    color: '#9a6b48',
    fontSize: '0.8rem',
    background: '#fff',
  },
  
  couponDivider: {
    width: '1px',
    margin: '18px 0',
    borderLeft: '1px dashed #dcc6b4',
  },
  
  couponRight: {
    width: '100px',
    border: 'none',
    background: 'transparent',
    color: '#9a6b48',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
  },
  
  downloadIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    border: '2px solid #b28a6a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    marginBottom: '10px',
    lineHeight: 1,
  },
  
  downloadText: {
    fontSize: '1rem',
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  listBtn: {
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    border: '1px solid #212529',
    background: '#fff',
    color: '#212529',
    cursor: 'pointer',
    borderRadius: '0.25rem',
  },
};

export default NoticeDetail;